import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetPaymentPlansViewParams = ViewDataParams & { view_id: string };

export type PaymentPlansView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

const emptyPaymentPlans: PaymentPlansView = { data: [], metadata: undefined };

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Forward-compatible: when the backend exposes a dedicated list endpoint, only the
// queryFn here changes — the consumer keeps the same hook contract.
export function useGetPaymentPlansView(params: GetPaymentPlansViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<PaymentPlansView, ApiError>({
    queryKey: ['payment-plans-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyPaymentPlans,
  });

  return addResourceNameToQueryResult<
    PaymentPlansView,
    ApiError,
    'paymentPlans',
    'PreventUndefined'
  >('paymentPlans', queryResult, emptyPaymentPlans);
}
